import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from './store/hooks';
import { initializeAuth } from './store/slices/authSlice';
import { initializePreferences } from './store/slices/preferencesSlice';
import { initializeDemoMetrics } from './store/slices/demoMetricsSlice';
import { useAuth } from './hooks/useAuth';
import { usePreferences } from './hooks/usePreferences';
import { useSettings } from './hooks/useSettings';
import { useIdleTimeout } from './hooks/useIdleTimeout';


import { LandingPage } from './pages/LandingPage';



import { LoginPage } from './modules/login/pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import  OrdersPage  from './pages/OrdersPage';
import SubmitOrderPage from './pages/SubmitOrderPage';
import  VerificationsPage  from './pages/VerificationsPage';
import DemographicsPage from './pages/DemographicsPage';
import InvoicingPage from './pages/InvoicingPage';
import PdfViewerPage from './pages/PdfViewerPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import  InsurancePage  from './pages/InsurancePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProtectedRoute } from './components/AuthProtectedRoute';
import { ForgotPassword } from './modules/auth/components/ForgotPassword';
import { ResetPassword } from './modules/auth/components/ResetPassword';
import PasswordResetRequired from './components/PasswordResetRequired';
import ConfigurationPage from './modules/configuration/pages/ConfigurationPage';
import { SimpleHeader } from './components/SimpleHeader';

import { LeftNavbar } from './components/LeftNavbar';
import { HeaderNavBar } from './components/HeaderNavBar';
import { IdleWarningModal } from './components/IdleWarningModal';
import './styles/App.css';
import './styles/typography.css';
import InventoryManagementPage from './pages/InventoryManagementPage';
import ProductManagementPage from './pages/ProductManagement';


const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize Redux state first, before using any hooks
  useEffect(() => {
    dispatch(initializeAuth());
    dispatch(initializePreferences());
    dispatch(initializeDemoMetrics());
  }, [dispatch]);

  const { user, isLoading, isAuthenticated } = useAuth();
  const { preferences, loadPreferences, changeNavigationLayout, isLoading: preferencesLoading } = usePreferences();
  const { settings, loadSettings } = useSettings();

  // Add a simple test to see if React is working

  // Idle timeout configuration based on user settings
  const idleTimeoutMinutes = settings?.security?.idleTimeout || 10;
  const isIdleTimeoutEnabled = settings?.security?.enableIdleTimeout ?? true;

  const {
    showWarning,
    timeRemaining,
    handleStayLoggedIn,
    handleLogoutNow,
  } = useIdleTimeout({
    timeout: isIdleTimeoutEnabled ? idleTimeoutMinutes * 60 * 1000 : 0, // Convert minutes to milliseconds
    warningTime: 30 * 1000, // 30 seconds warning
    onIdle: () => {
      // User has been idle, logging out
    },
    onActive: () => {
      // User activity detected, resetting idle timeout
    },
    onWarning: () => {
      // Idle warning popup shown
    },
  });

  useEffect(() => {
    // Load settings when user is authenticated

    // Listen for navigation layout changes from settings
    const handleNavigationChange = (event: CustomEvent) => {
      changeNavigationLayout(event.detail.layout);
    };

    window.addEventListener('navigationLayoutChange', handleNavigationChange as EventListener);

    return () => {
      window.removeEventListener('navigationLayoutChange', handleNavigationChange as EventListener);
    };
  }, []); // Empty dependency array - only run once

  // Load preferences from database when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('Loading preferences for authenticated user:', user.email);
      loadPreferences();
    }
  }, [isAuthenticated, user, loadPreferences]);

  // Load settings from database when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('Loading settings for authenticated user:', user.email);
      loadSettings();
    }
  }, [isAuthenticated, user, loadSettings]);

  // Apply font size class to body element when settings change
  useEffect(() => {
    if (settings?.display?.fontSize) {
      // Remove all existing font size classes
      document.body.classList.remove('font-size-small', 'font-size-medium', 'font-size-large', 'font-size-extra-large');

      // Add the current font size class
      document.body.classList.add(`font-size-${settings.display.fontSize}`);

      console.log('Applied font size class:', `font-size-${settings.display.fontSize}`);
    }
  }, [settings?.display?.fontSize]);

  // Handle page refresh - redirect to login if not authenticated (but allow public routes)
  useEffect(() => {
    const publicRoutes = ['/', '/page', '/login', '/forgot-password', '/reset-password', '/about'];
    const isPdfViewer = location.pathname.startsWith('/view-pdf/');

    if (!isLoading && !isAuthenticated && !publicRoutes.includes(location.pathname) && !isPdfViewer) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate]);

  // Show loading state while authentication and preferences are being checked
  if (isLoading || preferencesLoading) {
    return (
      <div className="app">
        <div className="main-content" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  // Check if user needs to reset password
  if (isAuthenticated && user?.needsPasswordChange) {
    return <PasswordResetRequired />;
  }

  // Public routes that should always be accessible (regardless of authentication status)
  const publicRoutes = ['/', '/page', '/login', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // PDF viewer route - uses cookie-based auth, doesn't need Redux authentication check
  const isPdfViewerRoute = location.pathname.startsWith('/view-pdf/');

  // If it's a public route, render it regardless of authentication status
  if (isPublicRoute) {
    return (
      <div className="app">
        {/* Show SimpleHeader for all public routes except login page */}
        {location.pathname === '/login' || location.pathname === '/' ? null : <SimpleHeader />}
        {/* <div className="main-content"> */}
          <Routes>
            {/* Public routes - always accessible */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/page" element={<Navigate to="/" replace />} />
            <Route
              path="/login"
              element={(
                <ProtectedRoute>
                  <LoginPage />
                </ProtectedRoute>
              )}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Routes>
        {/* </div> */}
      </div>
    );
  }

  // If it's a PDF viewer route, render it without authentication checks (uses cookie auth)
  if (isPdfViewerRoute) {
    return (
      <div className="app">
        <Routes>
          <Route path="/view-pdf/:type/:id" element={<PdfViewerPage />} />
        </Routes>
      </div>
    );
  }

  // Show SimpleHeader for public routes (including login), RoleBasedHeader for authenticated routes
  // This ensures login page always shows simple header regardless of previous authentication state

  // Determine navigation layout from Redux preferences
  const navigationLayout = preferences?.navigationLayout || 'sidebar';

  if (navigationLayout === 'sidebar' && isAuthenticated) {
    return (
      <div className="app sidebar-layout">
        <LeftNavbar />
        {/* <SidebarNavigation /> */}
        <div className="main-content-with-sidebar">
          <Routes>
            <Route path="/dashboard" element={
              user?.role === 'admin' ? 
              <Navigate to="/user-management" replace /> : 
              <DashboardPage />
            } />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/submitOrder/:orderId" element={<SubmitOrderPage />} />
            <Route path="/submitOrder/order/:orderId" element={<SubmitOrderPage />} />
            <Route path="/submitOrder/demographics/:demographicsId" element={<SubmitOrderPage />} />
            <Route path="/verifications" element={<VerificationsPage />} />
            <Route path="/demographics" element={<DemographicsPage />} />
            <Route path="/invoices" element={<InvoicingPage />} />
            <Route path="/view-pdf/:type/:id" element={<PdfViewerPage />} />
            <Route path="/insurance" element={<InsurancePage />} />
            <Route path="/inventory-dashboard" element={<InventoryManagementPage />} />
            <Route path="/product-management" element={<ProductManagementPage />} />
            <Route 
              path="/user-management" 
              element={
                <AuthProtectedRoute requiredRole="admin">
                  <UserManagementPage />
                </AuthProtectedRoute>
              } 
            />
            <Route 
              path="/configuration" 
              element={
                <AuthProtectedRoute requiredRole="admin">
                  <ConfigurationPage />
                </AuthProtectedRoute>
              } 
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={
              user?.role === 'admin' ? 
              <Navigate to="/user-management" replace /> : 
              <Navigate to="/dashboard" replace />
            } />
          </Routes>
        </div>

        {/* Idle Warning Modal */}
        <IdleWarningModal
          isVisible={showWarning}
          timeRemaining={timeRemaining}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleLogoutNow}
        />
      </div>
    );
  }

  if (navigationLayout === 'header' && isAuthenticated) {
    return (
      <div className="app header-layout">
        <HeaderNavBar />
        <div className="main-content-with-header">
          <Routes>
            <Route path="/dashboard" element={
              user?.role === 'admin' ? 
              <Navigate to="/user-management" replace /> : 
              <DashboardPage />
            } />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/submitOrder/:orderId" element={<SubmitOrderPage />} />
            <Route path="/submitOrder/order/:orderId" element={<SubmitOrderPage />} />
            <Route path="/submitOrder/demographics/:demographicsId" element={<SubmitOrderPage />} />
            <Route path="/verifications" element={<VerificationsPage />} />
            <Route path="/demographics" element={<DemographicsPage />} />
            <Route path="/invoices" element={<InvoicingPage />} />
            <Route path="/view-pdf/:type/:id" element={<PdfViewerPage />} />
            <Route path="/insurance" element={<InsurancePage />} />
            <Route path="/inventory-dashboard" element={<InventoryManagementPage />} />
            <Route path="/product-management" element={<ProductManagementPage />} />
            <Route 
              path="/user-management" 
              element={
                <AuthProtectedRoute requiredRole="admin">
                  <UserManagementPage />
                </AuthProtectedRoute>
              } 
            />
            <Route 
              path="/configuration" 
              element={
                <AuthProtectedRoute requiredRole="admin">
                  <ConfigurationPage />
                </AuthProtectedRoute>
              } 
            />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={
              user?.role === 'admin' ? 
              <Navigate to="/user-management" replace /> : 
              <Navigate to="/dashboard" replace />
            } />
          </Routes>
        </div>

        {/* Idle Warning Modal */}
        <IdleWarningModal
          isVisible={showWarning}
          timeRemaining={timeRemaining}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleLogoutNow}
        />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Show SimpleHeader for all public routes except login page */}
      {location.pathname === '/login'||location.pathname === '/' ? null : <SimpleHeader />}
      <div className="main-content">
        <Routes>
          {/* Protected routes - require authentication */}
          <Route 
            path="/dashboard" 
            element={
              <AuthProtectedRoute preventAdminAccess>
                <DashboardPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/page" 
            element={<Navigate to="/" replace />}
          />

          <Route 
            path="/orders" 
            element={
              <AuthProtectedRoute>
                <OrdersPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/verifications" 
            element={
              <AuthProtectedRoute>
                <VerificationsPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/demographics" 
            element={
              <AuthProtectedRoute>
                <DemographicsPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/invoices" 
            element={
              <AuthProtectedRoute>
                <InvoicingPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/insurance" 
            element={
              <AuthProtectedRoute>
                <InsurancePage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/inventory-dashboard" 
            element={
              <AuthProtectedRoute>
                <InventoryManagementPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/product-management" 
            element={
              <AuthProtectedRoute>
                <ProductManagementPage />
              </AuthProtectedRoute>
            } 
          />
          <Route 
            path="/user-management" 
            element={
              <AuthProtectedRoute requiredRole="admin">
                <UserManagementPage />
              </AuthProtectedRoute>
            } 
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/view-pdf/:type/:id" element={<PdfViewerPage />} />
          <Route path="*" element={
            user?.role === 'admin' ? 
            <Navigate to="/settings" replace /> : 
            <Navigate to="/dashboard" replace />
          } />
        </Routes>
      </div>

      {/* Idle Warning Modal */}
      <IdleWarningModal
        isVisible={showWarning}
        timeRemaining={timeRemaining}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogoutNow}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
};

// Simple Error Boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Something went wrong</h1>
          <p>Error: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default App;
